/**
 * Message Input Component - Enhanced with Modern Design
 */
import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Zoom,
  CircularProgress,
  keyframes,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MicIcon from '@mui/icons-material/Mic';

import useChatStore from '../store/chatStore';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';

export default function MessageInput() {
  const { user } = useAuthStore();
  const { sendMessage, isStreaming } = useChatStore();
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation(['common', 'chat']);

  const departmentColor = user?.department === 'HR' ? '#1976d2' : '#2e7d32';
  const departmentGradient = user?.department === 'HR'
    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
    : 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)';

  const handleSend = async () => {
    const message = input.trim();
    if (!message || isStreaming) return;

    setInput('');
    await sendMessage(message);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  `;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        background: 'linear-gradient(to top, #ffffff, #f8f9fa)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-end',
        }}
      >
        {/* Attachment button (placeholder) */}
        <Tooltip title={t('chat:actions.attachComingSoon')}>
          <IconButton
            size="medium"
            sx={{
              color: 'text.secondary',
              transition: 'all 0.2s',
              '&:hover': {
                color: departmentColor,
                transform: 'scale(1.1)',
              },
            }}
            disabled
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>

        {/* Text input */}
        <TextField
          fullWidth
          multiline
          maxRows={6}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={t('common:placeholders.messageInput')}
          disabled={isStreaming}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: 'white',
              transition: 'all 0.3s ease',
              boxShadow: isFocused
                ? `0 0 0 3px ${departmentColor}20`
                : '0 2px 8px rgba(0,0,0,0.05)',
              '& fieldset': {
                borderColor: isFocused ? departmentColor : 'rgba(0, 0, 0, 0.12)',
                borderWidth: isFocused ? '2px' : '1px',
                transition: 'all 0.3s ease',
              },
              '&:hover fieldset': {
                borderColor: departmentColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: departmentColor,
              },
              '& textarea': {
                fontSize: '0.95rem',
                lineHeight: 1.6,
              },
            },
            '& .MuiOutlinedInput-input': {
              '&::placeholder': {
                color: 'text.secondary',
                opacity: 0.6,
              },
            },
          }}
        />

        {/* Voice input button (placeholder) */}
        <Tooltip title={t('chat:actions.voiceComingSoon')}>
          <IconButton
            size="medium"
            sx={{
              color: 'text.secondary',
              transition: 'all 0.2s',
              '&:hover': {
                color: departmentColor,
                transform: 'scale(1.1)',
              },
            }}
            disabled
          >
            <MicIcon />
          </IconButton>
        </Tooltip>

        {/* Send button */}
        <Zoom in={!!input.trim() || isStreaming}>
          <Tooltip title={isStreaming ? t('chat:actions.sending') : t('chat:actions.send')}>
            <span>
              <IconButton
                onClick={handleSend}
                disabled={!input.trim() || isStreaming}
                size="large"
                sx={{
                  background: departmentGradient,
                  color: 'white',
                  width: 50,
                  height: 50,
                  boxShadow: `0 4px 14px ${departmentColor}40`,
                  transition: 'all 0.3s ease',
                  animation: isStreaming ? `${pulse} 1.5s ease-in-out infinite` : 'none',
                  '&:hover': {
                    background: departmentGradient,
                    transform: 'scale(1.1)',
                    boxShadow: `0 6px 20px ${departmentColor}60`,
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                    color: 'rgba(255, 255, 255, 0.6)',
                  },
                }}
              >
                {isStreaming ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Zoom>
      </Box>

      {/* Character counter (optional) */}
      {input.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 1,
            opacity: 0.6,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '0.75rem',
              color: input.length > 5000 ? 'error.main' : 'text.secondary',
            }}
          >
            {t('common:message.characterCount', { count: input.length })}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

